import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

const ConfirmContext = createContext(null);

export const ConfirmProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);
  const resolverRef = useRef(null);

  const close = useCallback((result) => {
    setDialog(null);
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  }, []);

  const confirm = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setDialog({
        title: options.title || 'Confirmation',
        message,
        confirmText: options.confirmText || 'OK',
        cancelText: options.cancelText || 'Annuler',
        variant: options.variant || 'danger',
      });
    });
  }, []);

  const api = useMemo(() => ({ confirm }), [confirm]);

  return (
    <ConfirmContext.Provider value={api}>
      {children}
      {dialog && (
        <div className="ui-dialog-overlay" role="dialog" aria-modal="true" aria-label={dialog.title} onClick={() => close(false)}>
          <div className="ui-dialog-container" onClick={(e) => e.stopPropagation()}>
            <div className="ui-dialog-header">
              <div className="ui-dialog-title">
                <h2>{dialog.title}</h2>
              </div>
              <button
                type="button"
                onClick={() => close(false)}
                aria-label="Close"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  border: '1px solid #e5e7eb',
                  background: '#fff',
                  fontSize: 22,
                  lineHeight: '38px',
                  cursor: 'pointer',
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '1.25rem 1.5rem' }}>
              <p style={{ color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>{dialog.message}</p>
              <div className="ui-dialog-actions" style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
                <button type="button" className="ui-btn ui-btn-secondary" onClick={() => close(false)}>
                  {dialog.cancelText}
                </button>
                <button
                  type="button"
                  className={
                    dialog.variant === 'danger'
                      ? 'ui-btn bg-red-600 text-white hover:bg-red-700'
                      : 'ui-btn-primary'
                  }
                  onClick={() => close(true)}
                >
                  {dialog.confirmText}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider');
  return ctx;
};

export default ConfirmContext;
